/**
 * Profit Machine Strategy — faithful port of MT4 Profit_Machine.mq4
 *
 * Combines two sub-indicators that must agree for a valid signal:
 *
 *   1. CCI-based Trend Magic — CCI on HLC/3; LONG when CCI crosses above 0,
 *      SHORT when it crosses below.  Forward-filled.
 *   2. Bollinger Bands Trailing Stop — BB on close; LONG when price breaks
 *      above upper band, SHORT below lower band.  Bands trail (never move
 *      against position).  Money-risk adjustment applied to stops.
 *
 * When both agree → new signal:
 *   Entry  = current close
 *   StopLoss = BB trailing stop level
 *   Target1 = entry ± 1.0 × risk   (1:1 RR)
 *   Target2 = entry ± 2.0 × risk   (1:2 RR)
 *
 * MT4 features ported:
 *   - Buy/Sell arrow markers at signal transitions (arrow codes 233/234)
 *   - Custom colored candles: GREEN (long), MAROON (short), WHITE (neutral)
 *   - Neutral state detection (BB ≠ TM → candle = neutral color)
 *   - Target / Stop Loss hit detection with ✓ tick marks
 *   - Live P/L and hit status tracking
 *   - Full dashboard metadata matching MT4 Panel class layout
 *   - Entry/SL/TP label markers on chart bars (like reference platform)
 *
 * Plots:
 *   - BB trailing stop curve (orange = long / red = short)
 *   - Markers: BUY/SELL arrows + Entry/SL/T1/T2 labels + hit markers
 *
 * Category: Strategies | Pane: Overlay
 */
import { IndicatorDefinition, PlotData, IndicatorMarker, IndicatorPriceLine } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeSMA } from "./sma";
import { inferTickSize, roundToTick } from "./price-utils";

// ─── Constants (matching MT4 ETradeSignal enum) ─────────────────
const TS_NONE = 0;
const TS_LONG = 1;
const TS_SHORT = 2;
type TSignal = typeof TS_NONE | typeof TS_LONG | typeof TS_SHORT;

// ─── Bollinger Band helpers ─────────────────────────────────────
function computeBBands(
  closes: number[],
  period: number,
  stdDevMult: number,
): { upper: number[]; lower: number[] } {
  const sma = computeSMA(closes, period);
  const upper: number[] = new Array(closes.length).fill(0);
  const lower: number[] = new Array(closes.length).fill(0);

  for (let i = period - 1; i < closes.length; i++) {
    const m = sma[i];
    if (m === undefined) continue;
    let sumSq = 0;
    for (let j = i - period + 1; j <= i; j++) {
      const d = closes[j] - m;
      sumSq += d * d;
    }
    const sd = Math.sqrt(sumSq / period);
    upper[i] = m + stdDevMult * sd;
    lower[i] = m - stdDevMult * sd;
  }
  return { upper, lower };
}

// ─── CCI computation (MT4 standard: HLC/3, mean deviation) ──────
function computeCCI(candles: CandleData[], period: number): number[] {
  const hlc3 = candles.map(c => (c.high + c.low + c.close) / 3);
  const cci: number[] = new Array(candles.length).fill(0);

  for (let i = period - 1; i < candles.length; i++) {
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += hlc3[j];
    const mean = sum / period;

    let devSum = 0;
    for (let j = i - period + 1; j <= i; j++) devSum += Math.abs(hlc3[j] - mean);
    const meanDev = devSum / period;

    cci[i] = meanDev !== 0 ? (hlc3[i] - mean) / (0.015 * meanDev) : 0;
  }
  return cci;
}

// ─── Dashboard data interface (matching MT4 Panel class) ─────────
export interface ProfitMachineDashboard {
  signal: "BUY" | "SELL" | null;
  trend: "LONG" | "SHORT" | "NEUTRAL";
  entry: number | null;
  stopLoss: number | null;
  target1: number | null;
  target2: number | null;
  currentPrice: number;
  livePL: number;
  scriptName: string;
  status: "Active" | "Target1 Hit" | "Target2 Hit" | "Stop Loss Hit";
  tp1Hit: boolean;
  tp2Hit: boolean;
  slHit: boolean;
  /** Candle coloring state for the current bar */
  candleState: "long" | "short" | "neutral";
}

export const profitMachineIndicator: IndicatorDefinition = {
  id: "profit_machine",
  name: "Profit Machine",
  shortName: "PM",
  category: "strategies",
  pane: "overlay",
  params: [
    { key: "alertEnabled", label: "Alert Notification", type: "boolean", default: true },
    { key: "displayLevels", label: "Display Lines", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
    { key: "alertEntry", label: "Alert on Entry", type: "boolean", default: true },
    { key: "alertTP1", label: "Alert on TP1 Hit", type: "boolean", default: true },
    { key: "alertTP2", label: "Alert on TP2 Hit", type: "boolean", default: true },
    { key: "alertSL", label: "Alert on SL Hit", type: "boolean", default: true },
    { key: "lineWidth", label: "Line Width", type: "number", default: 1, min: 1, max: 4, step: 1 },
    { key: "showBackground", label: "Candle Color", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
  ],
  plots: [
    { key: "entry", label: "Entry", color: "#1565C0", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "stopLoss", label: "Stop Loss", color: "#C62828", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "target1", label: "Target 1", color: "#2E7D32", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "target2", label: "Target 2", color: "#2E7D32", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const cciPeriod = 50;
    const bbPeriod = 10;
    const bbDeviation = 5;
    const moneyRisk = 1.0;
    const displayLevels = params.displayLevels ?? "yes";
    const len = candles.length;
    const minBars = Math.max(cciPeriod, bbPeriod) + 2;

    const emptyResult: PlotData[] = [
      { key: "entry", data: [] },
      { key: "stopLoss", data: [] },
      { key: "target1", data: [] },
      { key: "target2", data: [] },
    ];

    if (len < minBars) return emptyResult;

    // ════════════════════════════════════════════════════════════════
    // 1. CCI-based Trend Magic (MT4: TM_SIGNAL with forward-fill)
    //    Cross(CCI, 0) → LONG; Cross(0, CCI) → SHORT
    // ════════════════════════════════════════════════════════════════
    const cci = computeCCI(candles, cciPeriod);
    const tmSignal: TSignal[] = new Array(len).fill(TS_NONE);

    for (let i = 1; i < len; i++) {
      if (cci[i - 1] <= 0 && cci[i] > 0) tmSignal[i] = TS_LONG;
      else if (cci[i - 1] >= 0 && cci[i] < 0) tmSignal[i] = TS_SHORT;
      else tmSignal[i] = tmSignal[i - 1]; // forward-fill
    }

    // ════════════════════════════════════════════════════════════════
    // 2. Bollinger Bands Trailing Stop (matching MT4 exactly)
    //    Price breaks determine direction; bands trail; money-risk adj
    // ════════════════════════════════════════════════════════════════
    const closes = candles.map(c => c.close);
    const { upper: bbUpper, lower: bbLower } = computeBBands(closes, bbPeriod, bbDeviation);

    const bbSignal: TSignal[] = new Array(len).fill(TS_NONE);
    const bbStop: number[] = new Array(len).fill(0);

    let bbState: TSignal = TS_NONE;
    let prevBbU = bbUpper[0] || closes[0];
    let prevBbL = bbLower[0] || closes[0];
    let prevSlLong = prevBbL;
    let prevSlShort = prevBbU;

    for (let i = 1; i < len; i++) {
      const close = candles[i].close;

      // Price breaks determine signal
      if (close > prevBbU) bbState = TS_LONG;
      if (close < prevBbL) bbState = TS_SHORT;

      let curU = bbUpper[i] || prevBbU;
      let curL = bbLower[i] || prevBbL;

      // Trail bands — never move against position
      if (bbState === TS_LONG && curL < prevBbL) curL = prevBbL;
      if (bbState === TS_SHORT && curU > prevBbU) curU = prevBbU;

      // Money-risk adjustment (MT4 formula)
      const range = curU - curL;
      const adj = ((moneyRisk - 1.0) / 2.0) * range;
      let slLong = curL - adj;
      let slShort = curU + adj;

      // Trail stops — never move against position
      if (bbState === TS_LONG && slLong < prevSlLong) slLong = prevSlLong;
      if (bbState === TS_SHORT && slShort > prevSlShort) slShort = prevSlShort;

      bbSignal[i] = bbState;
      bbStop[i] =
        bbState === TS_LONG ? slLong : bbState === TS_SHORT ? slShort : bbStop[i - 1];

      prevBbU = curU;
      prevBbL = curL;
      prevSlLong = slLong;
      prevSlShort = slShort;
    }

    // ════════════════════════════════════════════════════════════════
    // 3. Signal generation — both TM and BB must agree (MT4 loop)
    //    MT4: "if m_bb_signal == TS_LONG && m_TM_PrevSignal == TS_LONG"
    // ════════════════════════════════════════════════════════════════
    const tick = inferTickSize(candles);
    const lw = Math.max(1, Math.min(4, Math.round(params.lineWidth ?? 1)));
    const showBackground = params.showBackground ?? "yes";
    const markers: IndicatorMarker[] = [];
    /** Arrow badge data stored in metadata for HTML overlay rendering */
    const arrowMarkers: { time: number; direction: "long" | "short"; price: number }[] = [];

    // Per-bar arrays (MT4-style forward-fill)
    const SIGNAL: TSignal[] = new Array(len).fill(TS_NONE);
    const ENTRY: number[] = new Array(len).fill(0);
    const SL: number[] = new Array(len).fill(0);
    const TP1: number[] = new Array(len).fill(0);
    const TP2: number[] = new Array(len).fill(0);
    // Track per-bar candle color state
    const candleColorState: ("long" | "short" | "neutral")[] = new Array(len).fill("neutral");

    let prevSignalType: TSignal = TS_NONE;
    let lastSignalBar = -1;
    let isNeutralCandle = true;

    // MT4: only calculate on new candle (skip forming bar = last bar)
    const lastClosedBar = len - 2;

    for (let i = 0; i <= lastClosedBar; i++) {
      const bb = bbSignal[i];
      const tm = tmSignal[i];

      if (bb === tm && bb !== TS_NONE) {
        const isNew = bb !== prevSignalType;

        if (isNew) {
          SIGNAL[i] = bb;
          ENTRY[i] = roundToTick(candles[i].close, tick);
          SL[i] = roundToTick(bbStop[i], tick);
          const risk = Math.abs(ENTRY[i] - SL[i]);

          // MT4 Profit Machine uses 1:1 and 1:2 risk-reward ratios
          if (bb === TS_LONG) {
            TP1[i] = roundToTick(ENTRY[i] + 1.0 * risk, tick);
            TP2[i] = roundToTick(ENTRY[i] + 2.0 * risk, tick);
          } else {
            TP1[i] = roundToTick(ENTRY[i] - 1.0 * risk, tick);
            TP2[i] = roundToTick(ENTRY[i] - 2.0 * risk, tick);
          }

          prevSignalType = bb;
          lastSignalBar = i;
          isNeutralCandle = false;

          // Arrow markers — MT4 arrow codes 233 (up) / 234 (down)
          // text is empty — badge labels rendered via HTML overlay for proper styling
          if (bb === TS_LONG) {
            markers.push({
              time: candles[i].time,
              position: "belowBar",
              color: "#006400",    // DarkGreen — matches long candle color
              shape: "arrowUp",
              text: "",
              size: 2,
            });
            arrowMarkers.push({ time: candles[i].time, direction: "long", price: candles[i].low });
          } else {
            markers.push({
              time: candles[i].time,
              position: "aboveBar",
              color: "#800000",    // Maroon — matches short candle color
              shape: "arrowDown",
              text: "",
              size: 2,
            });
            arrowMarkers.push({ time: candles[i].time, direction: "short", price: candles[i].high });
          }
        } else {
          // Same signal — carry forward
          SIGNAL[i] = i > 0 ? SIGNAL[i - 1] : TS_NONE;
          ENTRY[i] = i > 0 ? ENTRY[i - 1] : 0;
          SL[i] = i > 0 ? SL[i - 1] : 0;
          TP1[i] = i > 0 ? TP1[i - 1] : 0;
          TP2[i] = i > 0 ? TP2[i - 1] : 0;
          isNeutralCandle = false;
        }

        // Set candle color state
        candleColorState[i] = bb === TS_LONG ? "long" : "short";
      } else {
        // Disagreement between TM and BB — neutral state
        SIGNAL[i] = prevSignalType;
        if (i > 0) {
          ENTRY[i] = ENTRY[i - 1];
          SL[i] = SL[i - 1];
          TP1[i] = TP1[i - 1];
          TP2[i] = TP2[i - 1];
        }
        isNeutralCandle = true;
        candleColorState[i] = "neutral";
      }
    }

    // Forward-fill to last bar (forming bar)
    if (lastClosedBar >= 0 && lastClosedBar < len - 1) {
      for (let i = lastClosedBar + 1; i < len; i++) {
        SIGNAL[i] = SIGNAL[lastClosedBar];
        ENTRY[i] = ENTRY[lastClosedBar];
        SL[i] = SL[lastClosedBar];
        TP1[i] = TP1[lastClosedBar];
        TP2[i] = TP2[lastClosedBar];
        candleColorState[i] = candleColorState[lastClosedBar];
      }
    }

    // ════════════════════════════════════════════════════════════════
    // 4. Target / SL hit detection (MT4: Panel::Evaluate logic)
    //    MT4 checks HighestSince/LowestSince for each level
    // ════════════════════════════════════════════════════════════════
    let highSinceSignal = 0;
    let lowSinceSignal = Infinity;
    let status: ProfitMachineDashboard["status"] = "Active";
    let tp1Hit = false;
    let tp2Hit = false;
    let slHit = false;

    if (lastSignalBar >= 0) {
      // Include live bar (len-1) so status updates in real-time on price touch
      for (let i = lastSignalBar; i < len; i++) {
        highSinceSignal = Math.max(highSinceSignal, candles[i].high);
        lowSinceSignal = Math.min(lowSinceSignal, candles[i].low);
      }

      const entryVal = ENTRY[lastSignalBar];
      const slVal = SL[lastSignalBar];
      const tp1Val = TP1[lastSignalBar];
      const tp2Val = TP2[lastSignalBar];

      if (SIGNAL[lastSignalBar] === TS_LONG) {
        if (highSinceSignal >= tp2Val) {
          status = "Target2 Hit";
          tp1Hit = true;
          tp2Hit = true;
        } else if (highSinceSignal >= tp1Val) {
          status = "Target1 Hit";
          tp1Hit = true;
        }
        if (lowSinceSignal <= slVal) {
          status = "Stop Loss Hit";
          slHit = true;
        }
      } else if (SIGNAL[lastSignalBar] === TS_SHORT) {
        if (lowSinceSignal <= tp2Val) {
          status = "Target2 Hit";
          tp1Hit = true;
          tp2Hit = true;
        } else if (lowSinceSignal <= tp1Val) {
          status = "Target1 Hit";
          tp1Hit = true;
        }
        if (highSinceSignal >= slVal) {
          status = "Stop Loss Hit";
          slHit = true;
        }
      }
    }

    // ════════════════════════════════════════════════════════════════
    // 5. Dashboard metadata (matching MT4 Panel class fields)
    // ════════════════════════════════════════════════════════════════
    const uiBar = Math.max(0, len - 2);
    const currentPrice = candles[len - 1]?.close ?? 0;
    const currSignal = SIGNAL[uiBar];
    const currEntry = ENTRY[uiBar];
    const currSL = SL[uiBar];
    const currTP1 = TP1[uiBar];
    const currTP2 = TP2[uiBar];
    const currBB = bbSignal[uiBar];
    const currTM = tmSignal[uiBar];

    let dashSignal: ProfitMachineDashboard["signal"] = null;
    let dashTrend: ProfitMachineDashboard["trend"] = "NEUTRAL";
    let livePL = 0;

    if (currSignal !== TS_NONE && currEntry > 0) {
      if (currSignal === TS_LONG) {
        dashSignal = "BUY";
        dashTrend = "LONG";
        livePL = currentPrice - currEntry;
      } else {
        dashSignal = "SELL";
        dashTrend = "SHORT";
        livePL = currEntry - currentPrice;
      }

      // Neutral when BB and TM disagree (MT4: m_IsNeutralCandle detection)
      if (currBB !== currTM || currBB === TS_NONE) {
        dashTrend = "NEUTRAL";
      }
    }

    const dashboard: ProfitMachineDashboard = {
      signal: dashSignal,
      trend: dashTrend,
      entry: currEntry > 0 ? currEntry : null,
      stopLoss: currSL > 0 ? currSL : null,
      target1: currTP1 > 0 ? currTP1 : null,
      target2: currTP2 > 0 ? currTP2 : null,
      currentPrice,
      livePL,
      scriptName: "", // filled from chart panel symbol
      status,
      tp1Hit,
      tp2Hit,
      slHit,
      candleState: candleColorState[uiBar] || "neutral",
    };

    // ════════════════════════════════════════════════════════════════
    // 6. Build level line series from signal bar onward + candle colors
    // ════════════════════════════════════════════════════════════════
    const entryData: { time: number; value: number }[] = [];
    const slData: { time: number; value: number }[] = [];
    const t1Data: { time: number; value: number }[] = [];
    const t2Data: { time: number; value: number }[] = [];

    // Plot series intentionally left empty — priceLines handle all level drawing.

    // Build candle color map for ChartPanel to apply per-candle coloring
    // MT4 colors: DarkGreen for long, Maroon for short, White for neutral
    const candleColors: { time: number; color: string }[] = [];
    if (showBackground !== "no") for (let i = 0; i < len; i++) {
      const state = candleColorState[i];
      if (state === "long") {
        candleColors.push({ time: candles[i].time, color: "#006400" });  // DarkGreen
      } else if (state === "short") {
        candleColors.push({ time: candles[i].time, color: "#800000" });  // Maroon
      } else {
        candleColors.push({ time: candles[i].time, color: "#B0BEC5" });  // Neutral grey-white
      }
    }

    // Helper to format price values
    const fmt = (v: number) => v.toFixed(2);

    // ── Hit markers: place on the ACTUAL bar where target/SL was hit ──
    // These markers are correctly bar-relative (above/below the candle they occur on)
    if (lastSignalBar >= 0 && (tp1Hit || tp2Hit || slHit)) {
      const sigDir = SIGNAL[lastSignalBar];
      const entryVal = ENTRY[lastSignalBar];
      const slVal = SL[lastSignalBar];
      const tp1Val = TP1[lastSignalBar];
      const tp2Val = TP2[lastSignalBar];
      let _tp1Hit = tp1Hit, _tp2Hit = tp2Hit, _slHit = slHit;

      for (let i = lastSignalBar + 1; i <= Math.min(lastClosedBar, len - 1); i++) {
        if (sigDir === TS_LONG) {
          if (_tp1Hit && candles[i].high >= tp1Val) {
            markers.push({
              time: candles[i].time,
              position: "aboveBar",
              shape: "circle",
              color: "#2E7D32",
              text: `✓ T1`,
              size: 1,
            });
            _tp1Hit = false;
          }
          if (_tp2Hit && candles[i].high >= tp2Val) {
            markers.push({
              time: candles[i].time,
              position: "aboveBar",
              shape: "circle",
              color: "#2E7D32",
              text: `✓ T2`,
              size: 1,
            });
            _tp2Hit = false;
          }
          if (_slHit && candles[i].low <= slVal) {
            markers.push({
              time: candles[i].time,
              position: "belowBar",
              shape: "circle",
              color: "#C62828",
              text: `✗ SL`,
              size: 1,
            });
            _slHit = false;
          }
        } else if (sigDir === TS_SHORT) {
          if (_tp1Hit && candles[i].low <= tp1Val) {
            markers.push({
              time: candles[i].time,
              position: "belowBar",
              shape: "circle",
              color: "#2E7D32",
              text: `✓ T1`,
              size: 1,
            });
            _tp1Hit = false;
          }
          if (_tp2Hit && candles[i].low <= tp2Val) {
            markers.push({
              time: candles[i].time,
              position: "belowBar",
              shape: "circle",
              color: "#2E7D32",
              text: `✓ T2`,
              size: 1,
            });
            _tp2Hit = false;
          }
          if (_slHit && candles[i].high >= slVal) {
            markers.push({
              time: candles[i].time,
              position: "aboveBar",
              shape: "circle",
              color: "#C62828",
              text: `✗ SL`,
              size: 1,
            });
            _slHit = false;
          }
        }
      }
    }

    // ── Badge price-line labels for axis (Entry / SL / T1 / T2) ──
    // lineVisible:false → only the colored axis badge is drawn, no extra line
    const priceLines: IndicatorPriceLine[] = [];
    if (displayLevels !== "no" && currEntry > 0 && lastSignalBar >= 0) {
      const entryTime = candles[lastSignalBar].time;
      priceLines.push(
        { id: "pm-entry", price: currEntry, color: "#1565C0", title: "Entry",     lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
        { id: "pm-sl",    price: currSL,    color: "#C62828", title: "Stop Loss", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
        { id: "pm-t1",   price: currTP1,   color: "#2E7D32", title: "Target_1",  lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
        { id: "pm-t2",   price: currTP2,   color: "#2E7D32", title: "Target_2",  lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
      );
    }

    return [
      { key: "entry", data: entryData, markers, priceLines, metadata: { dashboard, candleColors, arrowMarkers } },
      { key: "stopLoss", data: slData },
      { key: "target1", data: t1Data },
      { key: "target2", data: t2Data },
    ];
  },
};
